import axios from 'axios';

// Same-origin by default: the product serves the SPA from the API
// server, so an empty base is correct everywhere it deploys. Set
// VITE_API_URL only for a split-origin dev setup.
export const API_BASE = import.meta.env.VITE_API_URL || '';
console.log('API_BASE:', API_BASE);

let authToken = null;
let isRefreshing = false;
let refreshSubscribers = [];
// First-wins guard so a burst of parallel admin calls all 403ing
// MFA_REQUIRED triggers ONE navigation, not N. Resets naturally on the
// full page load the redirect causes.
let mfaRedirecting = false;

// Route an aal1 session into the 2FA step-up screen, preserving where
// the user was so we can send them back once they reach aal2. Same-page
// no-op when we're already on the step-up screen (belt-and-braces
// against a self-redirect loop).
function redirectToMfaStepUp() {
  if (mfaRedirecting) return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/' && params.get('mfa') === 'stepup') return;
  } catch (_) { /* URL parse failure — fall through and redirect */ }
  mfaRedirecting = true;
  const returnUrl = window.location.pathname + window.location.search;
  window.location.href = '/?mfa=stepup&returnUrl=' + encodeURIComponent(returnUrl);
}

// REQUEST TIMEOUT.
//
// Without one, axios waits forever: a request the server never answers (a
// starved connection pool, a proxy that dropped the socket) leaves the caller
// pending for the life of the tab. That is why a saturated dashboard sat on
// loading skeletons instead of showing errors — the cards had nothing to
// render an error FROM.
//
// Sits ABOVE the dashboard aggregation's own query ceiling (110s —
// ZSD_QUERY_TIMEOUT_MS in the ZSD dashboards aggregation layer, which
// in turn sits just under Supabase's 2min statement_timeout) so the server
// always gets to answer its own timeout with a 504 and an actionable message;
// only a request that never lands at all trips this one. Keep this the larger
// of the two if either is retuned.
export const REQUEST_TIMEOUT_MS = 150_000;

// Endpoints that legitimately run for minutes and must NOT inherit the default:
// the Jira clone + its audit, a console script (server cap 600s), a full backup
// export, bulk work-item writes, and file uploads (a big attachment on a slow
// link is a long request, not a hung one). Matched on the request URL; anything
// not listed gets REQUEST_TIMEOUT_MS.
const NO_TIMEOUT_PATHS = [
  '/api/stride-cloner/clone',
  '/api/stride-cloner/runs/', // .../audit — the post-clone verification sweep
  '/api/field-map/scan',      // the mapping page's scan walks the projects' fields
  '/api/script-console/run',
  '/api/backup/export',
  '/api/issues/bulk-edit',
  '/api/issues/bulk-delete',
  '/attachments',
];

export const client = axios.create({
  baseURL: API_BASE,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Subscribe to token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers that token has been refreshed
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// Notify all subscribers that refresh failed
const onRefreshFailed = () => {
  refreshSubscribers.forEach(callback => callback(null));
  refreshSubscribers = [];
};

// Decode a JWT payload without verifying (verification happens
// server-side; we only need to read authProvider/exp client-side).
function decodeTokenPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch (e) {
    return null;
  }
}

// Single-flight token refresh shared by the 401/410 response
// interceptor and the proactive sliding renewal below. All live tokens
// are Supabase-issued app JWTs, renewed via /api/auth/renew (re-mints a
// fresh 7d JWT off the still-valid current one). Resolves with the new
// token and notifies queued subscribers; throws on failure (callers
// decide whether that means logout).
async function performTokenRefresh() {
  isRefreshing = true;
  try {
    const response = await client.post('/api/auth/renew', { token: authToken });
    const newToken = response.data.token;
    authToken = newToken;
    localStorage.setItem('token', newToken);
    onTokenRefreshed(newToken);
    return newToken;
  } catch (err) {
    onRefreshFailed();
    throw err;
  } finally {
    isRefreshing = false;
  }
}

// Proactive sliding renewal: Supabase-path JWTs live 7 days; once a
// stored token is older than ~3 days (less than 4 days of life left)
// any outgoing request kicks off a background renew so active users
// never hit the 7d expiry wall. Fire-and-forget — never blocks the
// request, and a failed background renew is retried by a later
// request (or handled by the 401 response interceptor once the token
// actually expires).
const RENEW_THRESHOLD_MS = 4 * 24 * 60 * 60 * 1000;

function maybeRenewSupabaseToken(url) {
  if (isRefreshing) return;
  if (url && url.includes('/api/auth/')) return;
  const payload = decodeTokenPayload(authToken);
  if (payload?.authProvider !== 'supabase' || !payload.exp) return;
  // Impersonation (act) tokens are deliberately non-renewable (the server
  // 400s them) — skip so we don't fire a rejected /api/auth/renew on every
  // request during a 60-min impersonation session.
  if (payload.act) return;
  const msLeft = payload.exp * 1000 - Date.now();
  if (msLeft <= 0 || msLeft >= RENEW_THRESHOLD_MS) return;
  performTokenRefresh().catch(err => {
    console.warn('Proactive token renewal failed (will retry on a later request):', err?.message || err);
  });
}

client.interceptors.request.use(config => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
    maybeRenewSupabaseToken(config.url);
  }
  // Long-running admin operations opt out of the default timeout (0 = none).
  // A caller that set its own timeout keeps it.
  const url = config.url || '';
  if (config.timeout === REQUEST_TIMEOUT_MS && NO_TIMEOUT_PATHS.some(p => url.includes(p))) {
    config.timeout = 0;
  }
  return config;
});

// Response interceptor to handle token expiration
client.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // TIMEOUT. axios raises ECONNABORTED (or ETIMEDOUT) with NO response, so
    // every downstream error formatter would fall through to the raw
    // "timeout of 50000ms exceeded". Tag it and rewrite the message once, here,
    // so a card/page renders a real error state (with its Retry) instead of
    // spinning forever or showing axios internals.
    if (!error.response && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')) {
      const secs = Math.round((originalRequest?.timeout || REQUEST_TIMEOUT_MS) / 1000);
      error.isTimeout = true;
      error.message = `The request timed out after ${secs}s. The server may be busy — try again, or narrow the query.`;
      return Promise.reject(error);
    }

    // Admin action hit while the session is aal1 and MFA enforcement is on:
    // the server returns 403 { code: 'MFA_REQUIRED' } (server.js requireAdmin
    // + jel-audit isRequestAdmin both use this exact code; the copy differs,
    // so match STRICTLY on code, not text). Redirect into the step-up screen.
    //
    // Excluded endpoints — these return MFA_REQUIRED as a NORMAL part of a
    // flow the page already handles, so hijacking them would break it or loop:
    //   /api/auth/exchange       — tryExchange enrols/challenges inline; if we
    //                              redirected here the working inline flow (and
    //                              the post-step-up re-exchange) would loop.
    //   /api/auth/supabase/login — legacy server login surfaces its own message
    //                              (it can't do browser MFA — see Home.jsx).
    //   /api/auth/renew          — handled in the 401/410 refresh catch below
    //                              so a single failure can't double-redirect.
    //   /api/auth/mfa*           — defensive; reserved for future step-up calls.
    if (error.response?.status === 403 && error.response?.data?.code === 'MFA_REQUIRED') {
      const url = originalRequest?.url || '';
      const inlineHandled = url.includes('/api/auth/exchange')
        || url.includes('/api/auth/supabase/login')
        || url.includes('/api/auth/renew')
        || url.includes('/api/auth/mfa');
      if (!inlineHandled) redirectToMfaStepUp();
      return Promise.reject(error);
    }

    // Check if error is due to expired token (410 Gone or 401 Unauthorized)
    if ((error.response?.status === 410 || error.response?.status === 401) &&
        !originalRequest._retry &&
        authToken &&
        !originalRequest.url.includes('/api/auth/')) {

      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(newToken => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(client(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      try {
        console.log('Token expired, attempting refresh...');
        const newToken = await performTokenRefresh();
        console.log('Token refreshed successfully');

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If this is an impersonation session that just expired (the
        // 60-min act-claim token can't be renewed — /api/auth/renew
        // refuses it with IMPERSONATION_NO_RENEW), drop the admin back
        // into their own session instead of logging them out entirely.
        const impersonatorToken = sessionStorage.getItem('zebra_impersonator_token');
        if (impersonatorToken) {
          sessionStorage.removeItem('zebra_impersonator_token');
          authToken = impersonatorToken;
          localStorage.setItem('token', impersonatorToken);
          setToken(impersonatorToken);
          window.location.href = '/home';
          return Promise.reject(refreshError);
        }
        // Renew refused because the aal1 session now needs MFA (an admin
        // flipped a group/domain/user requirement): the token is still
        // valid — send the user to step up rather than a bare logout, so
        // they keep their session and re-elevate. (The redirect goes
        // through /api/auth/exchange, which is excluded above, so no loop.)
        if (refreshError?.response?.status === 403 && refreshError?.response?.data?.code === 'MFA_REQUIRED') {
          redirectToMfaStepUp();
          return Promise.reject(refreshError);
        }
        // Clear token and redirect to login
        authToken = null;
        localStorage.removeItem('token');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// The one function that turns a failed call into the sentence the user
// reads. Pure and tested on its own (error-message.mjs); re-exported here so
// every existing import keeps working.
export { extractErrorMessage } from './error-message.mjs';

export function setToken(token) {
  authToken = token;
}

// For the rare call that must stream its answer (fetch + a ReadableStream,
// which axios cannot do): the same bearer the interceptor attaches.
export function getToken() {
  return authToken;
}
